import { createContext, useContext, useState, useEffect } from 'react';

const CustomizationContext = createContext();

export const defaultCustomization = {
  fontFamily: "'EB Garamond', serif",
  primaryColor: '#014d4e',
  primaryColor1: '#069093',
  primaryColor2: '#3b82f6',
  primaryColor3: '#60a5fa',
  primaryColor4: '#93c5fd',
  borderRadius: '12px',
  spacing: '1rem',
  animationSpeed: '0.3s',
  density: 'balanced',
  fontScale: 'normal',
  surfaceStyle: 'glass',
  visualDepth: 'soft',
  cursorStyle: 'accent',
  contrast: 'normal',
  contentWidth: 'balanced',
  motionMode: 'balanced'
};

const densityScale = {
  compact: '0.82',
  balanced: '1',
  spacious: '1.16'
};

const fontScale = {
  compact: '0.96',
  normal: '1',
  large: '1.08'
};

const contentWidth = {
  focused: '1040px',
  balanced: '1200px',
  wide: '1360px'
};

const hexToRgb = (hex) => {
  const normalized = hex?.replace('#', '');
  if (!normalized || !/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return '1, 77, 78';
  }

  const number = parseInt(normalized, 16);
  return `${(number >> 16) & 255}, ${(number >> 8) & 255}, ${number & 255}`;
};

const buildCursor = (hex, type = 'default') => {
  const color = /^[#][0-9a-fA-F]{6}$/.test(hex) ? hex : defaultCustomization.primaryColor;
  const isPointer = type === 'pointer';
  const size = isPointer ? 32 : 24;
  const center = size / 2;
  const outer = isPointer ? 12 : 8;
  const inner = isPointer ? 5 : 3;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${center}" cy="${center}" r="${outer}" fill="${color}" opacity="${isPointer ? 0.28 : 0.42}"/>
      <circle cx="${center}" cy="${center}" r="${inner}" fill="${color}"/>
    </svg>
  `.trim();

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${center} ${center}, ${isPointer ? 'pointer' : 'auto'}`;
};

const getSavedCustomization = () => {
  try {
    const saved = localStorage.getItem('customization');
    return saved ? { ...defaultCustomization, ...JSON.parse(saved) } : defaultCustomization;
  } catch (error) {
    return defaultCustomization;
  }
};

export const CustomizationProvider = ({ children }) => {
  const [customization, setCustomization] = useState(getSavedCustomization);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--font-family', customization.fontFamily);
    root.style.setProperty('--color-primary', customization.primaryColor);
    root.style.setProperty('--color-primary-1', customization.primaryColor1);
    root.style.setProperty('--color-primary-2', customization.primaryColor2);
    root.style.setProperty('--color-primary-3', customization.primaryColor3);
    root.style.setProperty('--color-primary-4', customization.primaryColor4);
    root.style.setProperty('--border-radius', customization.borderRadius);
    root.style.setProperty('--spacing', customization.spacing);
    root.style.setProperty('--animation-speed', customization.animationSpeed);
    root.style.setProperty('--color-primary-rgb', hexToRgb(customization.primaryColor));
    root.style.setProperty('--color-secondary', customization.primaryColor2);
    root.style.setProperty('--color-secondary-rgb', hexToRgb(customization.primaryColor2));
    root.style.setProperty('--custom-density-scale', densityScale[customization.density] || densityScale.balanced);
    root.style.setProperty('--custom-font-scale', fontScale[customization.fontScale] || fontScale.normal);
    root.style.setProperty('--custom-content-width', contentWidth[customization.contentWidth] || contentWidth.balanced);

    root.dataset.density = customization.density;
    root.dataset.fontScale = customization.fontScale;
    root.dataset.surfaceStyle = customization.surfaceStyle;
    root.dataset.depth = customization.visualDepth;
    root.dataset.cursor = customization.cursorStyle;
    root.dataset.contrast = customization.contrast;
    root.dataset.motion = customization.motionMode === 'reduced' ? 'reduced' : 'normal';

    if (customization.cursorStyle === 'native') {
      root.style.setProperty('--cursor-default', 'auto');
      root.style.setProperty('--cursor-pointer', 'pointer');
    } else if (customization.cursorStyle === 'minimal') {
      root.style.setProperty('--cursor-default', 'crosshair');
      root.style.setProperty('--cursor-pointer', 'pointer');
    } else {
      root.style.setProperty('--cursor-default', buildCursor(customization.primaryColor));
      root.style.setProperty('--cursor-pointer', buildCursor(customization.primaryColor, 'pointer'));
    }

    document.body.style.fontFamily = customization.fontFamily;
    
    try {
      localStorage.setItem('customization', JSON.stringify(customization));
    } catch (error) {
      // Keep customization working even if storage is unavailable.
    }
  }, [customization]);

  const updateCustomization = (key, value) => {
    setCustomization(prev => ({ ...prev, [key]: value }));
  };

  const updateCustomizations = (updates) => {
    setCustomization(prev => ({ ...prev, ...updates }));
  };

  const resetCustomization = () => {
    setCustomization(defaultCustomization);
  };

  return (
    <CustomizationContext.Provider value={{ customization, updateCustomization, updateCustomizations, resetCustomization }}>
      {children}
    </CustomizationContext.Provider>
  );
};

export const useCustomization = () => useContext(CustomizationContext);
